import Money from '@/common/models/money'
import { TRADING } from '@/common/strings'
import { InfoIconTooltip } from '@/common/ui/tooltip'
import { cn } from '@/common/utils/utils'
import { FC } from 'react'
import { OrderRewardItem } from './orderRewardItem'
import { Gift } from 'lucide-react'
import { REWARDS_COPY } from '@/locales/en'

type Props = {
    fee: Money
    amount: Money
    availableFunds: Money
    isSellOrder: boolean
    displayWithPrecision?: number
    freeTrade?: boolean
}

const Item = ({
    label,
    value,
    tooltip,
    className,
}: {
    label: string
    value: string
    tooltip?: string
    className?: string
}) => (
    <div className="self-stretch inline-flex justify-start items-center gap-1">
        <div className="flex items-center gap-2">
            <span className={cn("justify-start text-black text-sm font-['Inter'] leading-5", className)}>{label}:</span>
            {tooltip && <InfoIconTooltip>{tooltip}</InfoIconTooltip>}
        </div>
        <strong
            className={cn(
                "flex-1 text-right justify-start text-black text-base font-bold font-['Inter'] leading-6",
                className,
            )}
        >
            {value}
        </strong>
    </div>
)

export const TradeMoneySummary: FC<Props> = ({
    fee,
    amount,
    availableFunds,
    isSellOrder,
    displayWithPrecision = 2,
    freeTrade = false,
}) => {
    const amountInclFee = isSellOrder ? amount.subtract(fee) : amount.add(fee)

    return (
        <>
            <div className="w-full self-stretch inline-flex flex-col justify-start items-start gap-4">
                <div className="text-black text-xl font-semibold font-['Inter'] leading-7">Total</div>
                <Item
                    label="Estimated amount"
                    value={`${amount.toString(displayWithPrecision)}`}
                    tooltip={isSellOrder ? undefined : 'Estimated amount includes a 2% buffer for Market Orders'}
                />
                <div className="self-stretch inline-flex flex-col justify-center items-end gap-1">
                    <Item
                        className="font-normal"
                        label="Estimated brokerage"
                        value={`${fee.toString(displayWithPrecision)}`}
                        tooltip={TRADING.brokerageNote}
                    />
                    {freeTrade && (
                        <OrderRewardItem
                            className="mt-4"
                            icon={<Gift size={20} />}
                            title={REWARDS_COPY.freeTradeItem.title}
                            description={REWARDS_COPY.freeTradeItem.description}
                        />
                    )}
                </div>

                <div className="self-stretch h-0 outline outline-1 outline-offset-[-0.50px] outline-border-divider" />
                <Item label="Estimate Total" value={`${amountInclFee.toString(displayWithPrecision)}`} />
                {!isSellOrder && availableFunds.lessThan(amountInclFee) ? (
                    <p className="text-red-700 text-right text-sm">Not enough available funds</p>
                ) : null}
            </div>
        </>
    )
}
